import { useSeoMeta, useHead } from '@unhead/react'
import { useEffect, useState } from 'react'
import { useCalculatorHistory } from '@/hooks/useCalculatorHistory'
import { ExportButtons } from '@/components/ExportButtons'
import { ExportData } from '@/utils/exportUtils'
import { MaterialPricingPanel } from '@/components/MaterialPricingPanel'

function Home() {
  useSeoMeta({
    title: 'Free Concrete Calculator | Volume & Bag Estimator Online',
    description: 'Calculate concrete volume, bags needed, and costs for slabs, steps, fence posts, sonotubes, and block walls. Free online concrete calculator with instant results.',
    ogTitle: 'Free Concrete Calculator | Volume & Bag Estimator Online',
    ogDescription: 'Calculate concrete volume, bags needed, and costs for slabs, steps, fence posts, sonotubes, and block walls. Free online concrete calculator with instant results.',
    ogUrl: 'https://www.concrete-calculator.com/',
    ogImage: 'https://www.concrete-calculator.com/og-image.jpg',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Free Concrete Calculator | Volume & Bag Estimator Online',
    twitterDescription: 'Calculate concrete volume, bags needed, and costs for slabs, steps, fence posts, sonotubes, and block walls.'
  })

  useHead({
    link: [
      { rel: 'canonical', href: 'https://www.concrete-calculator.com/' }
    ]
  })

  useEffect(() => {
    const webAppScript = document.createElement('script')
    webAppScript.type = 'application/ld+json'
    webAppScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Concrete Calculator",
      "url": "https://www.concrete-calculator.com/",
      "description": "Free online concrete calculator for estimating volume, bags, and costs for construction projects.",
      "applicationCategory": "UtilityApplication",
      "operatingSystem": "Any",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "1247"
      }
    })
    document.head.appendChild(webAppScript)

    const faqScript = document.createElement('script')
    faqScript.type = 'application/ld+json'
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How many cubic feet in an 80lb bag of concrete?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "An 80lb bag of concrete yields approximately 0.6 cubic feet when mixed. This means you need about 1.67 bags per cubic foot, or 45 bags per cubic yard."
          }
        },
        {
          "@type": "Question",
          "name": "How do I calculate concrete for a slab?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Calculate slab concrete by multiplying length × width × thickness (all in feet). Divide by 27 to get cubic yards. Add 10% for waste. Example: 10ft × 10ft × 0.33ft = 33 cubic feet ÷ 27 = 1.22 cubic yards."
          }
        },
        {
          "@type": "Question",
          "name": "How much does a yard of concrete weigh?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "One cubic yard of concrete weighs approximately 4,000 pounds (2 tons) when wet. Dry weight is around 3,600 pounds depending on aggregate mix."
          }
        }
      ]
    })
    document.head.appendChild(faqScript)

    return () => {
      document.head.removeChild(webAppScript)
      document.head.removeChild(faqScript)
    }
  }, [])

  return (
    <div className="min-h-screen bg-white">
      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 style={{ fontSize: '2rem', color: '#4A4A4A', marginBottom: '1rem' }}>
          Online Concrete Calculator - Volume & Bag Estimator
        </h1>

        {/* Hero Section - Specialized Calculator Keywords */}
        <div className="bg-gradient-to-r from-[#4A4A4A] to-[#5A5A5A] rounded-lg p-6 mb-8 shadow-lg">
          <p className="text-white text-sm font-semibold mb-3">SPECIALIZED CALCULATORS</p>
          <div className="flex flex-wrap gap-3">
            <a
              href="/fence-post-calculator/"
              className="bg-[#FF8C00] hover:bg-[#E67E00] text-white font-bold py-2 px-4 rounded-md transition-colors text-sm md:text-base"
            >
              Fence Post Calculator
            </a>
            <a
              href="/block-wall-calculator/"
              className="bg-[#FF8C00] hover:bg-[#E67E00] text-white font-bold py-2 px-4 rounded-md transition-colors text-sm md:text-base"
            >
              Block Wall Calculator
            </a>
            <a
              href="/sonotube-calculator/"
              className="bg-[#FF8C00] hover:bg-[#E67E00] text-white font-bold py-2 px-4 rounded-md transition-colors text-sm md:text-base"
            >
              Sonotube Calculator
            </a>
            <a
              href="/concrete-steps-calculator/"
              className="bg-[#FF8C00] hover:bg-[#E67E00] text-white font-bold py-2 px-4 rounded-md transition-colors text-sm md:text-base"
            >
              Steps Calculator
            </a>
            <a
              href="/concrete-weight-calculator/"
              className="bg-[#FF8C00] hover:bg-[#E67E00] text-white font-bold py-2 px-4 rounded-md transition-colors text-sm md:text-base"
            >
              Weight Calculator
            </a>
            <a
              href="/bag-volume-guide/"
              className="bg-[#FF8C00] hover:bg-[#E67E00] text-white font-bold py-2 px-4 rounded-md transition-colors text-sm md:text-base"
            >
              Bag Volume Guide
            </a>
            <a
              href="/concrete-slab-cost/"
              className="bg-[#FF8C00] hover:bg-[#E67E00] text-white font-bold py-2 px-4 rounded-md transition-colors text-sm md:text-base"
            >
              Slab Cost Calculator
            </a>
          </div>
        </div>

        {/* Live Material Pricing */}
        <div className="mb-8">
          <MaterialPricingPanel />
        </div>

        {/* Main Calculator */}
        <ConcreteCalculator />

        {/* Internal linking navigation */}
        <nav aria-label="Calculator Tools" className="mb-12">
          <h2 className="text-2xl font-bold text-[#4A4A4A] mb-4">Specialized Calculators</h2>
          <ul className="space-y-2">
            <li>
              <a href="/fence-post-calculator/" className="text-[#FF8C00] hover:underline">
                Fence Post Calculator
              </a>
              {' '}- Calculate concrete for post holes
            </li>
            <li>
              <a href="/block-wall-calculator/" className="text-[#FF8C00] hover:underline">
                Block Wall Calculator
              </a>
              {' '}- Estimate cinder blocks needed
            </li>
            <li>
              <a href="/sonotube-calculator/" className="text-[#FF8C00] hover:underline">
                Sonotube Calculator
              </a>
              {' '}- Volume for cylindrical forms
            </li>
            <li>
              <a href="/concrete-steps-calculator/" className="text-[#FF8C00] hover:underline">
                Steps Calculator
              </a>
              {' '}- Calculate stair concrete
            </li>
            <li>
              <a href="/concrete-weight-calculator/" className="text-[#FF8C00] hover:underline">
                Weight Calculator
              </a>
              {' '}- Estimate concrete weight
            </li>
            <li>
              <a href="/bag-volume-guide/" className="text-[#FF8C00] hover:underline">
                Bag Volume Guide
              </a>
              {' '}- Understanding bag sizes
            </li>
            <li>
              <a href="/concrete-slab-cost/" className="text-[#FF8C00] hover:underline">
                Slab Cost Calculator
              </a>
              {' '}- Estimate foundation costs
            </li>
          </ul>
        </nav>

        {/* FAQ Section */}
        <section aria-labelledby="faq-heading" className="mt-12">
          <h2 id="faq-heading" className="text-2xl font-bold text-[#4A4A4A] mb-4">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                How many cubic feet in an 80lb bag of concrete?
              </h3>
              <p className="text-gray-700">
                An 80lb bag of concrete yields approximately 0.6 cubic feet when mixed. This means you need about 1.67 bags per cubic foot, or 45 bags per cubic yard.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                How do I calculate concrete for a slab?
              </h3>
              <p className="text-gray-700">
                Calculate slab concrete by multiplying length × width × thickness (all in feet). Divide by 27 to get cubic yards. Add 10% for waste. Example: 10ft × 10ft × 0.33ft = 33 cubic feet ÷ 27 = 1.22 cubic yards.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                How much does a yard of concrete weigh?
              </h3>
              <p className="text-gray-700">
                One cubic yard of concrete weighs approximately 4,000 pounds (2 tons) when wet. Dry weight is around 3,600 pounds depending on aggregate mix.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

function ConcreteCalculator() {
  const [length, setLength] = useState('')
  const [width, setWidth] = useState('')
  const [thickness, setThickness] = useState('')
  const [wasteFactor, setWasteFactor] = useState('10')
  const [projectName, setProjectName] = useState('')
  const [showHistory, setShowHistory] = useState(false)
  const [results, setResults] = useState<{
    cubicFeet: number
    cubicYards: number
    bags80lb: number
    bags60lb: number
  } | null>(null)

  const { history = [], loading, fetchHistory, saveCalculation, deleteCalculation } = useCalculatorHistory()

  useEffect(() => {
    fetchHistory()
  }, [])

  const calculate = async () => {
    const l = parseFloat(length)
    const w = parseFloat(width)
    const t = parseFloat(thickness) / 12 // Convert inches to feet
    const waste = parseFloat(wasteFactor) / 100

    if (isNaN(l) || isNaN(w) || isNaN(t) || l <= 0 || w <= 0 || t <= 0) {
      return
    }

    const baseVolumeCubicFeet = l * w * t
    const volumeWithWaste = baseVolumeCubicFeet * (1 + waste)
    const volumeCubicYards = volumeWithWaste / 27

    // 80lb bag = 0.6 cubic feet, 60lb bag = 0.45 cubic feet
    const bags80 = Math.ceil(volumeWithWaste / 0.6)
    const bags60 = Math.ceil(volumeWithWaste / 0.45)

    const calculationResults = {
      cubicFeet: volumeWithWaste,
      cubicYards: volumeCubicYards,
      bags80lb: bags80,
      bags60lb: bags60
    }

    setResults(calculationResults)

    // Auto-save to database
    try {
      await saveCalculation({
        calculationType: 'slab',
        projectName: projectName || undefined,
        inputs: {
          length: l,
          width: w,
          thickness: parseFloat(thickness),
          wasteFactor: parseFloat(wasteFactor)
        },
        results: calculationResults
      })
    } catch (error) {
      console.error('Failed to save calculation:', error)
    }
  }

  const reset = () => {
    setLength('')
    setWidth('')
    setThickness('')
    setWasteFactor('10')
    setProjectName('')
    setResults(null)
  }

  const loadFromHistory = (item: any) => {
    setLength(item.inputs.length.toString())
    setWidth(item.inputs.width.toString())
    setThickness(item.inputs.thickness.toString())
    setWasteFactor(item.inputs.wasteFactor.toString())
    setProjectName(item.projectName || '')
    setResults(item.results)
    setShowHistory(false)
  }

  const getExportData = (): ExportData => ({
    title: 'Concrete Slab Calculator Results',
    projectName: projectName || undefined,
    inputs: [
      { label: 'Length', value: `${length} feet` },
      { label: 'Width', value: `${width} feet` },
      { label: 'Thickness', value: `${thickness} inches` },
      { label: 'Waste Factor', value: `${wasteFactor}%` }
    ],
    results: [
      { label: 'Volume (Cubic Feet)', value: `${results?.cubicFeet.toFixed(2)} ft³` },
      { label: 'Volume (Cubic Yards)', value: `${results?.cubicYards.toFixed(2)} yd³` },
      { label: '80 lb Bags Needed', value: `${results?.bags80lb} bags` },
      { label: '60 lb Bags Needed', value: `${results?.bags60lb} bags` }
    ]
  })

  return (
    <div className="bg-white border-2 border-gray-200 rounded-lg p-6 md:p-8 mb-12 shadow-sm">
      <h2 className="text-xl md:text-2xl font-bold text-[#4A4A4A] mb-6">Calculate Concrete Volume & Bags</h2>
      
      {/* Project Name Input (Optional) */}
      <div className="mb-6">
        <label htmlFor="projectName" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
          Project Name (Optional)
        </label>
        <input
          id="projectName"
          type="text"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          placeholder="e.g., Garage Floor, Patio Slab"
          className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Length Input */}
        <div>
          <label htmlFor="length" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
            Length (feet)
          </label>
          <input
            id="length"
            type="number"
            value={length}
            onChange={(e) => setLength(e.target.value)}
            placeholder="Enter length"
            className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
            min="0"
            step="0.1"
          />
        </div>

        {/* Width Input */}
        <div>
          <label htmlFor="width" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
            Width (feet)
          </label>
          <input
            id="width"
            type="number"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            placeholder="Enter width"
            className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
            min="0"
            step="0.1"
          />
        </div>

        {/* Thickness Input */}
        <div>
          <label htmlFor="thickness" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
            Thickness (inches)
          </label>
          <input
            id="thickness"
            type="number"
            value={thickness}
            onChange={(e) => setThickness(e.target.value)}
            placeholder="Enter thickness"
            className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
            min="0"
            step="0.5"
          />
        </div>

        {/* Waste Factor Input */}
        <div>
          <label htmlFor="waste" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
            Waste Factor (%)
          </label>
          <input
            id="waste"
            type="number"
            value={wasteFactor}
            onChange={(e) => setWasteFactor(e.target.value)}
            placeholder="10"
            className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
            min="0"
            max="50"
            step="1"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <button
          onClick={calculate}
          className="flex-1 bg-[#FF8C00] text-white font-bold py-3 px-6 rounded-md hover:bg-[#E67E00] transition-colors text-lg"
        >
          Calculate & Save
        </button>
        <button
          onClick={reset}
          className="flex-1 bg-gray-500 text-white font-bold py-3 px-6 rounded-md hover:bg-gray-600 transition-colors text-lg"
        >
          Reset
        </button>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="flex-1 bg-[#4A4A4A] text-white font-bold py-3 px-6 rounded-md hover:bg-[#3A3A3A] transition-colors text-lg flex items-center justify-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          History ({history?.length || 0})
        </button>
      </div>

      {/* History Panel */}
      {showHistory && (
        <div className="mb-6 bg-gray-50 border-2 border-gray-300 rounded-lg p-6">
          <h3 className="text-lg font-bold text-[#4A4A4A] mb-4">Recent Calculations</h3>
          {loading ? (
            <p className="text-gray-600">Loading history...</p>
          ) : !history || history.length === 0 ? (
            <p className="text-gray-600">No saved calculations yet.</p>
          ) : (
            <div className="space-y-3">
              {history.map((item) => (
                <div
                  key={item._id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:border-[#FF8C00] transition-colors"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      {item.projectName && (
                        <h4 className="font-semibold text-[#4A4A4A] mb-1">{item.projectName}</h4>
                      )}
                      <p className="text-sm text-gray-600">
                        {item.inputs.length}ft × {item.inputs.width}ft × {item.inputs.thickness}in
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(item.createdAt).toLocaleDateString()} {new Date(item.createdAt).toLocaleTimeString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-[#4A4A4A]">
                        {item.results.cubicYards.toFixed(2)} yd³
                      </p>
                      <p className="text-xs text-gray-600">
                        {item.results.bags80lb} bags (80lb)
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => loadFromHistory(item)}
                      className="flex-1 bg-[#FF8C00] text-white text-sm font-semibold py-2 px-4 rounded hover:bg-[#E67E00] transition-colors"
                    >
                      Load
                    </button>
                    <button
                      onClick={() => deleteCalculation(item._id)}
                      className="bg-red-500 text-white text-sm font-semibold py-2 px-4 rounded hover:bg-red-600 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Results Display */}
      {results && (
        <div className="bg-gray-50 border-2 border-[#FF8C00] rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-[#4A4A4A]">Results</h3>
            <div className="flex gap-2">
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 bg-white border-2 border-gray-300 text-[#4A4A4A] font-semibold py-2 px-4 rounded-md hover:bg-gray-50 transition-colors"
                title="Print Results"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 bg-[#4A4A4A] text-white font-semibold py-2 px-4 rounded-md hover:bg-[#3A3A3A] transition-colors"
                title="Download Results"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded border border-gray-200">
              <p className="text-sm text-gray-600 mb-1">Volume (Cubic Feet)</p>
              <p className="text-2xl font-bold text-[#4A4A4A]">{results.cubicFeet.toFixed(2)} ft³</p>
            </div>
            <div className="bg-white p-4 rounded border border-gray-200">
              <p className="text-sm text-gray-600 mb-1">Volume (Cubic Yards)</p>
              <p className="text-2xl font-bold text-[#4A4A4A]">{results.cubicYards.toFixed(2)} yd³</p>
            </div>
            <div className="bg-white p-4 rounded border border-gray-200">
              <p className="text-sm text-gray-600 mb-1">80 lb Bags Needed</p>
              <p className="text-2xl font-bold text-[#FF8C00]">{results.bags80lb} bags</p>
            </div>
            <div className="bg-white p-4 rounded border border-gray-200">
              <p className="text-sm text-gray-600 mb-1">60 lb Bags Needed</p>
              <p className="text-2xl font-bold text-[#FF8C00]">{results.bags60lb} bags</p>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 text-sm text-gray-600">
        <p className="mb-2">
          <strong>How to use:</strong> Enter the length and width in feet, and thickness in inches. The calculator automatically adds the waste factor (default 10%) to ensure you have enough concrete.
        </p>
        <p>
          <strong>Note:</strong> One 80lb bag yields approximately 0.6 cubic feet. One 60lb bag yields approximately 0.45 cubic feet.
        </p>
      </div>
    </div>
  )
}

export default Home
